import React, { useState, useRef, useEffect } from 'react';
import { LogOut } from 'lucide-react';

export interface UserProfileCardProps {
  avatarUrl?: string;
  displayName: string;
  email: string;
  collapsed?: boolean;
  onSignOut?: () => void;
  className?: string;
}

export const UserProfileCard: React.FC<UserProfileCardProps> = ({
  avatarUrl,
  displayName,
  email,
  collapsed = false,
  onSignOut,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const togglePopover = () => {
    setIsOpen(!isOpen);
  };

  const handleSignOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSignOut?.();
    setIsOpen(false);
  };

  return (
    <div
      ref={cardRef}
      className={`relative bg-white/5 border border-white/10 backdrop-blur-sm rounded-lg p-3 mx-3 mb-3 hover:bg-white/10 cursor-pointer transition-colors ${className}`}
      onClick={togglePopover}
    >
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-brand-400 font-semibold text-sm shrink-0 overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            displayName.charAt(0).toUpperCase()
          )}
        </div>

        {/* User Info - Hidden when collapsed */}
        {!collapsed && (
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="text-white text-sm font-medium truncate">{displayName}</div>
            <div className="text-slate-400 text-xs truncate">{email}</div>
          </div>
        )}

        {/* Sign Out Icon - Shown when expanded */}
        {!collapsed && (
          <div className="text-slate-400">
            <LogOut size={16} />
          </div>
        )}
      </div>

      {/* Popover Menu */}
      {isOpen && (
        <div className="absolute bottom-full left-0 w-full mb-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100">
          <button
            onClick={handleSignOut}
            className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-white/5 flex items-center gap-2 transition-colors"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};
